import React, { useEffect, useState, useCallback } from 'react';
import { View, Text, StyleSheet, ScrollView, TouchableOpacity, ActivityIndicator, RefreshControl, LayoutAnimation, Platform, UIManager, Dimensions } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { Ionicons, MaterialCommunityIcons } from '@expo/vector-icons';
import { Image } from 'expo-image';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { useTranslation } from 'react-i18next';
import { getProfile, getFollows, getFollowers, getLists, getSavedFeeds, getDid, removeFeed } from '../services/bluesky';
import { Theme, useTheme, useThemedStyles } from '../theme';
import { getFavorites, FavoriteUser } from '../utils/favorites';

if (Platform.OS === 'android' && UIManager.setLayoutAnimationEnabledExperimental) {
  UIManager.setLayoutAnimationEnabledExperimental(true);
}

const { width: SCREEN_WIDTH } = Dimensions.get('window');

interface Props {
  onSearch?: () => void;
  onSelectUser?: (did: string) => void;
  onSelectFeed?: (feed: any) => void;
  onSelectList?: (list: any) => void;
  onOpenNotifications?: () => void;
  onOpenSaved?: () => void;
  onOpenDrafts?: () => void;
  onOpenFeedSearch?: () => void;
}

type SectionKey = 'favorites' | 'followers' | 'following' | 'feeds' | 'lists';

const Home: React.FC<Props> = ({ onSelectUser, onSelectFeed, onSelectList, onSearch, onOpenNotifications, onOpenSaved, onOpenDrafts, onOpenFeedSearch }) => {
  const insets = useSafeAreaInsets();
  const [profile, setProfile] = useState<any>(null);
  const [follows, setFollows] = useState<any[]>([]);
  const [followers, setFollowers] = useState<any[]>([]);
  const [lists, setLists] = useState<any[]>([]);
  const [savedFeeds, setSavedFeeds] = useState<any[]>([]);
  const [favoriteUsers, setFavoriteUsers] = useState<FavoriteUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [expandedSections, setExpandedSections] = useState<Record<SectionKey, boolean>>({
    favorites: true,
    followers: false,
    following: true,
    feeds: false,
    lists: false,
  });
  const { theme } = useTheme();
  const { t } = useTranslation();
  const styles = useThemedStyles(createStyles);

  const loadCache = async () => {
    try {
      const did = getDid();
      if (!did) {
        console.warn('loadCache: No DID available, skipping cache load');
        return;
      }
      
      const CACHE_KEY = `home_cache_${did}`;
      const cached = await AsyncStorage.getItem(CACHE_KEY);
      if (cached) {
        const data = JSON.parse(cached);
        setProfile(data.profile);
        setFollows(data.follows);
        setFollowers(data.followers);
        setLists(data.lists);
        setSavedFeeds(data.savedFeeds);
        setLoading(false);
      }
      
      // Load favorite users (always fresh, not cached)
      const favUsers = await getFavorites();
      setFavoriteUsers(favUsers);
    } catch (e) {
      console.error('Failed to load home cache:', e);
    }
  };

  const fetchData = useCallback(async (isManual = false) => {
    if (isManual) setRefreshing(true);
    try {
      const did = getDid();
      if (!did) {
        console.warn('fetchData: No DID available, skipping');
        return;
      }

      const [profileData, followsData, followersData, listsData, feedsData, favUsers] = await Promise.all([
        getProfile(did),
        getFollows(did),
        getFollowers(did),
        getLists(did),
        getSavedFeeds(),
        getFavorites(),
      ]);

      const newData = {
        profile: profileData,
        follows: followsData.follows,
        followers: followersData.followers,
        lists: listsData,
        savedFeeds: feedsData,
      };

      setProfile(profileData);
      setFollows(followsData.follows);
      setFollowers(followersData.followers);
      setLists(listsData);
      setSavedFeeds(feedsData);
      setFavoriteUsers(favUsers);

      const CACHE_KEY = `home_cache_${did}`;
      await AsyncStorage.setItem(CACHE_KEY, JSON.stringify(newData));
    } catch (error) {
      console.error('Failed to fetch home data:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    loadCache().then(() => fetchData());
  }, [fetchData]);

  const onRefresh = () => {
    fetchData(true);
  };

  const handleRemoveFeed = async (feedUri: string) => {
    try {
      await removeFeed(feedUri);
      fetchData(); // Refresh data
    } catch (error) {
      console.error('Failed to remove feed:', error);
    }
  };

  const toggleSection = (key: SectionKey) => {
    LayoutAnimation.configureNext(LayoutAnimation.Presets.easeInEaseOut);
    setExpandedSections(prev => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  if (loading && !profile) {
    return (
      <View style={[styles.container, styles.centered]}>
        <ActivityIndicator size="large" color={theme.colors.primary} />
      </View>
    );
  }

  const favoriteFeeds = savedFeeds.filter(f => f.pinned);
  const favoriteLists = lists.filter(l => l.pinned);
  const favorites = [...favoriteUsers, ...favoriteFeeds, ...favoriteLists];
  
  const otherFeeds = savedFeeds.filter(f => !f.pinned);
  const otherLists = lists.filter(l => !l.pinned);

  const renderSectionHeader = (title: string, count: number, key: SectionKey) => (
    <TouchableOpacity 
      style={styles.sectionHeader} 
      onPress={() => {
        if (key === 'feeds' && onOpenFeedSearch) {
          onOpenFeedSearch();
        } else {
          toggleSection(key);
        }
      }}
      activeOpacity={0.7}
    >
      <View style={styles.sectionHeaderLeft}>
        {key === 'feeds' ? (
          <Ionicons 
            name="add-circle-outline" 
            size={16} 
            color={theme.colors.primary} 
            style={{ marginRight: 4 }}
          />
        ) : (
          <Ionicons 
            name={expandedSections[key] ? "chevron-down" : "chevron-forward"} 
            size={16} 
            color={theme.colors.textMuted} 
            style={{ marginRight: 4 }}
          />
        )}
        <Text style={styles.sectionTitle}>{title}</Text>
        <Text style={styles.sectionCount}>{count}</Text>
      </View>
    </TouchableOpacity>
  );

  return (
    <View style={[styles.container, { paddingTop: insets.top }]}>
      <View style={styles.header}>
        <Text style={styles.headerTitle}>{t('tabs.home')}</Text>
      </View>

      <ScrollView 
        style={styles.content}
        refreshControl={
          <RefreshControl refreshing={refreshing} onRefresh={onRefresh} tintColor={theme.colors.primary} />
        }
      >
        {/* Profile Section */}
        <View style={styles.profileCard}>
          <TouchableOpacity 
            style={styles.profileMain}
            onPress={() => profile && onSelectUser?.(profile.did)}
          >
            <Image 
              source={{ uri: profile?.avatar || 'https://picsum.photos/200' }} 
              style={styles.myAvatar}
              cachePolicy="memory-disk"
              priority="high"
            />
            <View style={styles.myInfo}>
              <Text style={styles.myName}>{profile?.displayName || profile?.handle || '読み込み中...'}</Text>
              <Text style={styles.myHandle}>@{profile?.handle}</Text>
              <Text style={styles.myStatus} numberOfLines={2}>
                {profile?.description || t('home.noDescription')}
              </Text>
            </View>
          </TouchableOpacity>
          
          <View style={styles.profileStats}>
            <TouchableOpacity style={styles.statBox} onPress={() => toggleSection('following')}>
              <Text style={styles.statValue}>{profile?.followsCount || 0}</Text>
              <Text style={styles.statLabel}>{t('profile.follow')}</Text>
            </TouchableOpacity>
            <TouchableOpacity style={styles.statBox} onPress={() => toggleSection('followers')}>
              <Text style={styles.statValue}>{profile?.followersCount || 0}</Text>
              <Text style={styles.statLabel}>{t('home.followers')}</Text>
            </TouchableOpacity>
            <View style={styles.statBox}>
              <Text style={styles.statValue}>{profile?.postsCount || 0}</Text>
              <Text style={styles.statLabel}>{t('profile.posts')}</Text>
            </View>
          </View>
        </View>

        {/* Quick Actions */}
        <View style={styles.quickActions}>
          <TouchableOpacity style={styles.actionItem} onPress={onOpenNotifications}>
            <View style={styles.actionIcon}> 
              <MaterialCommunityIcons name="bell-outline" size={24} color={theme.colors.primary} />
            </View>
            <Text style={styles.actionLabel}>{t('home.notifications')}</Text>
          </TouchableOpacity>
          <TouchableOpacity style={styles.actionItem} onPress={onOpenFeedSearch}> 
            <View style={styles.actionIcon}> 
              <MaterialCommunityIcons name="rss" size={24} color={theme.colors.primary} />
            </View>
            <Text style={styles.actionLabel}>{t('chatList.feed')}</Text>
          </TouchableOpacity>
          <TouchableOpacity style={styles.actionItem} onPress={onOpenSaved}>
            <View style={styles.actionIcon}> 
              <MaterialCommunityIcons name="bookmark-outline" size={24} color={theme.colors.primary} />
            </View>
            <Text style={styles.actionLabel}>{t('home.saved')}</Text>
          </TouchableOpacity>
          <TouchableOpacity style={styles.actionItem} onPress={onOpenDrafts}> 
            <View style={styles.actionIcon}> 
              <MaterialCommunityIcons name="file-document-edit-outline" size={24} color={theme.colors.primary} />
            </View>
            <Text style={styles.actionLabel}>{t('drafts.title')}</Text>
          </TouchableOpacity>
        </View>

        {/* Favorites Section */}
        {renderSectionHeader(t('home.favorites'), favorites.length, 'favorites')}
        {expandedSections.favorites && (
          favorites.length > 0 ? (
            favorites.map((item: any) => {
              // Check if it's a favorite user (has 'did' and 'handle' but no 'uri')
              const isFavoriteUser = item.did && item.handle && !item.uri;
              return (
                <TouchableOpacity 
                  key={isFavoriteUser ? item.did : item.uri} 
                  style={styles.listItem}
                  onPress={() => {
                    if (isFavoriteUser) {
                      onSelectUser?.(item.did);
                    } else if (item.purpose) {
                      onSelectList?.(item);
                    } else {
                      onSelectFeed?.(item);
                    }
                  }}
                >
                  <Image source={{ uri: item.avatar || 'https://picsum.photos/200' }} style={styles.listAvatar} cachePolicy="memory-disk" />
                  <View style={styles.listInfo}>
                    <Text style={styles.listName}>{item.displayName || item.name || item.handle}</Text>
                    <Text style={styles.listStatus} numberOfLines={1}>
                      {isFavoriteUser 
                        ? (item.description || `@${item.handle}`)
                        : item.purpose 
                        ? (item.description || t('chatList.list')) 
                        : `by @${item.creator?.handle}`}
                    </Text>
                  </View>
                  <Ionicons name="star" size={16} color="#FFB900" />
                </TouchableOpacity>
              );
            })
          ) : (
            <View style={styles.emptySection}>
              <Text style={styles.emptyText}>{t('home.noFavorites')}</Text>
            </View>
          )
        )}

        {/* Following Section */}
        {renderSectionHeader(t('home.following'), profile?.followsCount || follows.length, 'following')}
        {expandedSections.following && follows.map(friend => (
          <TouchableOpacity 
            key={friend.did} 
            style={styles.listItem}
            onPress={() => onSelectUser?.(friend.did)}
          >
            <Image source={{ uri: friend.avatar }} style={styles.listAvatar} cachePolicy="memory-disk" />
            <View style={styles.listInfo}>
              <Text style={styles.listName}>{friend.displayName || friend.handle}</Text>
              <Text style={styles.listStatus} numberOfLines={1}>{friend.description || friend.handle}</Text>
            </View>
          </TouchableOpacity>
        ))}

        {/* Followers Section */}
        {renderSectionHeader(t('home.followers'), profile?.followersCount || followers.length, 'followers')}
        {expandedSections.followers && followers.map(friend => (
          <TouchableOpacity 
            key={friend.did} 
            style={styles.listItem}
            onPress={() => onSelectUser?.(friend.did)}
          >
            <Image source={{ uri: friend.avatar }} style={styles.listAvatar} cachePolicy="memory-disk" />
            <View style={styles.listInfo}>
              <Text style={styles.listName}>{friend.displayName || friend.handle}</Text>
              <Text style={styles.listStatus} numberOfLines={1}>{friend.description || friend.handle}</Text>
            </View>
          </TouchableOpacity>
        ))}

        {/* Feeds Section */}
        {renderSectionHeader(t('home.feeds'), otherFeeds.length, 'feeds')}
        {expandedSections.feeds && otherFeeds.map(feed => (
          <TouchableOpacity 
            key={feed.uri} 
            style={styles.listItem}
            onPress={() => onSelectFeed?.(feed)}
          >
            <Image source={{ uri: feed.avatar }} style={styles.listAvatar} cachePolicy="memory-disk" />
            <View style={styles.listInfo}>
              <Text style={styles.listName}>{feed.displayName}</Text>
              <Text style={styles.listStatus} numberOfLines={1}>by @{feed.creator?.handle}</Text>
            </View>
            <TouchableOpacity 
              onPress={() => handleRemoveFeed(feed.uri)}
              style={{ padding: 8 }}
            >
              <Ionicons name="trash-outline" size={18} color={theme.colors.textMuted} />
            </TouchableOpacity>
          </TouchableOpacity>
        ))}

        {/* Lists Section */}
        {renderSectionHeader(t('home.lists'), otherLists.length, 'lists')}
        {expandedSections.lists && otherLists.map(list => (
          <TouchableOpacity 
            key={list.uri} 
            style={styles.listItem}
            onPress={() => onSelectList?.(list)}
          >
            <Image source={{ uri: list.avatar }} style={styles.listAvatar} cachePolicy="memory-disk" />
            <View style={styles.listInfo}>
              <Text style={styles.listName}>{list.name}</Text>
              <Text style={styles.listStatus} numberOfLines={1}>{list.description || t('chatList.list')}</Text>
            </View>
          </TouchableOpacity>
        ))}
        
        <View style={{ height: 40 }} />
      </ScrollView>
    </View>
  );
};

const createStyles = (theme: Theme) => StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: theme.colors.background,
  },
  centered: {
    justifyContent: 'center',
    alignItems: 'center',
  },
  header: {
    height: 60,
    backgroundColor: theme.colors.card,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    borderBottomWidth: 0.5,
    borderBottomColor: theme.colors.border,
  },
  headerTitle: {
    color: theme.colors.text,
    fontSize: 20,
    fontWeight: 'bold',
  },
  headerIcons: {
    flexDirection: 'row',
  },
  headerIcon: {
    marginLeft: 16,
  },
  content: {
    flex: 1,
  },
  profileCard: {
    backgroundColor: theme.colors.card,
    margin: 12,
    borderRadius: 12,
    padding: 16,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.05,
    shadowRadius: 4,
    elevation: 2,
    borderWidth: 0.5,
    borderColor: theme.colors.border,
  },
  profileMain: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  myAvatar: {
    width: 70,
    height: 70,
    borderRadius: 35,
    backgroundColor: theme.colors.cardSecondary,
  },
  myInfo: {
    marginLeft: 16,
    flex: 1,
  },
  myName: {
    fontSize: 18,
    fontWeight: 'bold',
    color: theme.colors.text,
  },
  myHandle: {
    fontSize: 13,
    color: theme.colors.textMuted,
    marginTop: 2,
  },
  myStatus: {
    fontSize: 13,
    color: theme.colors.textMuted,
    marginTop: 6,
    lineHeight: 18,
  },
  profileStats: {
    flexDirection: 'row',
    marginTop: 16,
    paddingTop: 16,
    borderTopWidth: 0.5,
    borderTopColor: theme.colors.border,
  },
  statBox: {
    flex: 1,
    alignItems: 'center',
  },
  statValue: {
    fontSize: 16,
    fontWeight: 'bold',
    color: theme.colors.text,
  },
  statLabel: {
    fontSize: 11,
    color: theme.colors.textMuted,
    marginTop: 2,
  },
  quickActions: {
    flexDirection: 'row',
    backgroundColor: theme.colors.card,
    paddingVertical: 16,
    marginBottom: 12,
    borderTopWidth: 0.5,
    borderBottomWidth: 0.5,
    borderColor: theme.colors.border,
  },
  actionItem: {
    flex: 1,
    alignItems: 'center',
  },
  actionIcon: {
    width: 48,
    height: 48,
    borderRadius: 20,
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 8,
    backgroundColor: theme.colors.cardSecondary,
  },
  actionLabel: {
    fontSize: 11,
    color: theme.colors.text,
  },
  sectionHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    paddingVertical: 12,
    backgroundColor: theme.colors.card,
    borderBottomWidth: 0.5,
    borderBottomColor: theme.colors.border,
    borderTopWidth: 0.5,
    borderTopColor: theme.colors.border,
  },
  sectionHeaderLeft: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  sectionTitle: {
    fontSize: 13,
    fontWeight: '600',
    color: theme.colors.text,
  },
  sectionCount: {
    fontSize: 13,
    color: theme.colors.textMuted,
    marginLeft: 6,
  },
  listItem: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 12,
    backgroundColor: theme.colors.card,
    borderBottomWidth: 0.5,
    borderBottomColor: theme.colors.border,
  },
  listAvatar: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: theme.colors.cardSecondary,
  },
  listInfo: {
    marginLeft: 12,
    flex: 1,
  },
  listName: {
    fontSize: 15,
    fontWeight: '500',
    color: theme.colors.text,
  },
  listStatus: {
    fontSize: 12,
    color: theme.colors.textMuted,
    marginTop: 2,
  },
  emptySection: {
    padding: 20,
    alignItems: 'center',
    backgroundColor: theme.colors.card,
    borderTopWidth: 0.5,
    borderTopColor: theme.colors.border,
    borderBottomWidth: 0.5,
    borderBottomColor: theme.colors.border,
  },
  emptyText: {
    fontSize: 13,
    color: theme.colors.textMuted,
  },
});

export default Home;
